import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/languagecontext';
import { API_BASE, authFetch } from '../services/api.js';
import CustomSelect from '../components/CustomSelect';

const TopicsSubtopicsInterface = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data passed from TeachingMode
  const passedData = location.state || {};
  const {
    selectedSubject: passedSubject,
    selectedChapter: passedChapter,
    language: passedLanguage
  } = passedData;

  // Language context
  const { currentLanguage, switchLanguage } = useLanguage();

  // Data states
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);

  // Load initial data
  useEffect(() => {
    if (passedSubject && passedChapter) {
      loadTopicsAndSubtopics();
    }
  }, []);

  // Reload content when language changes
  useEffect(() => {
    if (passedSubject && passedChapter) {
      loadTopicsAndSubtopics();
    }
  }, [currentLanguage]);

  const loadTopicsAndSubtopics = async () => {
    if (!passedSubject || !passedChapter) return;
    
    setLoading(true);
    try {
      // Load topics with language parameter
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
      const topicsResponse = await authFetch(`${API_BASE}/topics/?chapter_id=${passedChapter.chapter_id}&subject_id=${passedSubject.subject_id}&language=${currentLanguage}&student_id=${studentId}`);
      const topicsData = await topicsResponse.json();
      
      // Load subtopics for each topic
      const topicsWithSubtopics = await Promise.all(
        topicsData.map(async (topic) => {
          try {
            const subtopicsResponse = await authFetch(`${API_BASE}/subtopics/?topic_id=${topic.topic_id}&language=${currentLanguage}&student_id=${studentId}`);
            const subtopics = subtopicsResponse.ok ? await subtopicsResponse.json() : [];
            
            return {
              ...topic,
              subtopics: subtopics.map(st => ({
                ...st,
                status: 'not_started' // You can implement real status tracking
              })),
              status: 'not_started' // You can implement real status tracking
            };
          } catch (error) {
            console.error('Error loading subtopics:', error);
            return {
              ...topic,
              subtopics: [],
              status: 'not_started'
            };
          }
        })
      );
      
      setTopics(topicsWithSubtopics);
    } catch (error) {
      console.error('Failed to load topics:', error);
      setTopics([]);
    }
    setLoading(false);
  };

  // Status indicator component
  const StatusIndicator = ({ status }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'completed': return '#10B981';
        case 'in_progress': return '#F59E0B';
        default: return '#E5E7EB';
      }
    };

    return (
      <div 
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: getStatusColor(status)
        }}
      />
    );
  };

  const toggleTopicExpansion = (topicId) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
    }
    setExpandedTopics(newExpanded);
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedTopics(new Set());
    } else {
      setExpandedTopics(new Set(topics.map(topic => topic.topic_id)));
    }
    setExpandAll(!expandAll);
  };

  const navigateToChunks = (topic, subtopic) => {
    navigate('/enhanced-chunks', {
      state: {
        selectedSubject: passedSubject,
        selectedChapter: passedChapter,
        selectedTopic: topic,
        selectedSubtopic: subtopic,
        language: currentLanguage,
        viewAllChunks: !subtopic
      }
    });
  };

  const handleBackToChapters = () => {
    navigate('/teaching', {
      state: {
        returnToChapters: true,
        selectedSubject: passedSubject,
        language: currentLanguage
      }
    });
  };

  if (!passedSubject || !passedChapter) {
    return (
      <div style={{padding: '2rem', textAlign: 'center'}}>
        <h2>No subject or chapter selected</h2>
        <button onClick={() => navigate('/teaching')}>Go to Teaching Mode</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '15px 20px 15px 4rem',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h1 style={{margin: 0, fontSize: '1.5rem'}}>
            {passedChapter.name} - Topics
          </h1>
          <p style={{margin: '5px 0 0 0', opacity: 0.9}}>
            Subject: {passedSubject.name}
          </p>
        </div>
        
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
          {/* Language Selector */}
          <CustomSelect
            value={currentLanguage}
            onChange={(e) => switchLanguage(e.target.value)}
            options={[
              { value: 'en', label: '🇺🇸 English' },
              { value: 'hi', label: '🇮🇳 Hindi' }
            ]}
            placeholder="Select Language"
            className="language-dropdown-overlay"
          />
          

          <button onClick={handleExpandAll} style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}>
            {expandAll ? 'Collapse All' : 'Expand All'}
          </button>
          
          <button 
            onClick={handleBackToChapters}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Topics List */}
      <div style={{
        background: 'white',
        margin: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{padding: '40px', textAlign: 'center'}}>
            <div>Loading topics...</div>
          </div>
        ) : topics.length === 0 ? (
          <div style={{padding: '40px', textAlign: 'center', color: '#6b7280'}}>
            No topics found for this chapter
          </div>
        ) : (
          topics.map(topic => (
            <div key={topic.topic_id}>
              {/* FIRST ROW: TOPIC NAME */}
              <div style={{
                padding: '15px 20px',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: 'white'
                
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <StatusIndicator status={topic.status} />
                  <span style={{
                    flex: 1,
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '16px',
                    textAlign: 'left'
                  }}>
                    {topic.name}
                  </span>
                </div>
                {/* SECOND ROW: TWO BUTTONS */}  
                <div style={{  
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '10px',
                  marginTop: '10px',
                  paddingLeft: '24px' // Align with topic name
                }}>
                  {/* BUTTON 1: SUB TOPICS */} 
                  <button
                    onClick={() => toggleTopicExpansion(topic.topic_id)}
                    style={{
                      background: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Sub Topics {expandedTopics.has(topic.topic_id) ? '▼' : '▶'}
                  </button>
                
                  {/* BUTTON 2: VIEW ALL */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToChunks(topic, null);
                    }}
                    style={{
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    View All
                  </button>    
                </div>
              </div>

              {/* SUBTOPICS - Show when expanded */}
              {expandedTopics.has(topic.topic_id) && (
                <div style={{backgroundColor: '#f9fafb'}}>
                  {topic.subtopics.length === 0 ? (
                    <div style={{
                      padding: '15px 20px 15px 40px',
                      color: '#6b7280',
                      fontStyle: 'italic'
                    }}>
                      No subtopics available
                    </div>
                  ) : (
                    topic.subtopics.map(subtopic => (
                      <div 
                        key={subtopic.subtopic_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 20px 12px 40px',
                          gap: '12px',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onClick={() => navigateToChunks(topic, subtopic)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <StatusIndicator status={subtopic.status} />
                        <span style={{
                          flex: 1,
                          color: '#4b5563',
                          fontSize: '14px',
                          textAlign: 'left'
                        }}>
                          🔹 {subtopic.name}
                        </span>
                        <span style={{
                          color: '#667eea',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Start Learning →
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TopicsSubtopicsInterface;